"use client"

import { useCallback, useEffect, useState, type ReactNode } from "react"

import { Button } from "@/components/ui/button"

import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { ArrowLeft, X } from "lucide-react"
import type { BookingMode, BookingStepId } from "@/store/appointment-store"
import { useStore } from "@/app/components/common/store.provider"
import { refreshAppointmentPaymentMethods } from "@/lib/utils/payment-methods"
import { AppointmentCrumbs } from "./appointmentCrumbs"
import { BookingModeSelect } from "./BookingModeSelect"
import { StepGuests } from "./stepGuests"
import { Step1 } from "./step1"
import { Step2 } from "./step2"
import { Step3 } from "./step3"
import { AppointmentCart } from "./appointmentCart"
import { GroupAppointmentCart } from "./GroupAppointmentCart"
import { observer } from "mobx-react-lite"

type CustomerAppointmentModalProps = {
  trigger?: ReactNode
  defaultServiceId?: number
  onCreated?: () => void
  open?: boolean
  onOpenChange?: (open: boolean) => void
  hideTrigger?: boolean
}

const STEP_DESCRIPTIONS: Record<BookingStepId, string> = {
  guests: "Tell us who is coming.",
  services: "Choose your services.",
  time: "Choose your preferred date and time.",
  confirm: "Review your appointment details and confirm.",
}

function titleForMode(mode: BookingMode | null): string {
  if (mode === "group") return "Group Booking"
  if (mode === "single") return "Book Appointment"
  return "Book with us"
}

export const CustomerAppointmentModal = observer(function CustomerAppointmentModal({
  trigger,
  onCreated,
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
  hideTrigger = false,
}: CustomerAppointmentModalProps) {
  const { appointmentStore, authStore } = useStore()

  const [internalOpen, setInternalOpen] = useState(false)
  const open = controlledOpen ?? internalOpen

  const setOpen = useCallback(
    (nextOpen: boolean) => {
      if (controlledOnOpenChange) {
        controlledOnOpenChange(nextOpen)
      } else {
        setInternalOpen(nextOpen)
      }
    },
    [controlledOnOpenChange]
  )

  useEffect(() => {
    if (!open || !authStore.isAuthenticated) return
    appointmentStore.initNotificationsConsentFromUser(
      authStore.user?.notificationsConsent
    )
  }, [appointmentStore, authStore.isAuthenticated, authStore.user?.notificationsConsent, open])

  useEffect(() => {
    if (!open) return
    let mounted = true
    void refreshAppointmentPaymentMethods(appointmentStore).catch(() => {
      if (!mounted) return
    })
    return () => {
      mounted = false
    }
  }, [appointmentStore, open])

  const handleCreated = useCallback(() => {
    setOpen(false)
    onCreated?.()
  }, [onCreated, setOpen])

  const resetState = useCallback(() => {
    appointmentStore.reset()
  }, [appointmentStore])

  const mode = appointmentStore.bookingMode
  const stepId = appointmentStore.currentStepId
  const isGroup = appointmentStore.isGroupMode
  const isModeSelect = mode === null

  const handleBack = () => {
    if (isModeSelect) {
      setOpen(false)
      if (controlledOnOpenChange == null) resetState()
      return
    }
    if (appointmentStore.currentStep <= 1) {
      appointmentStore.setBookingMode(null)
      return
    }
    appointmentStore.previousStep()
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        setOpen(nextOpen)
        if (!nextOpen && controlledOnOpenChange == null) resetState()
      }}
    >
      {!hideTrigger && trigger ? <DialogTrigger asChild>{trigger}</DialogTrigger> : null}
      <DialogContent
        showCloseButton={false}
        className="fixed inset-0 left-0 top-0 z-50 m-0 flex h-dvh max-h-none w-screen max-w-none translate-x-0 translate-y-0 flex-col gap-0 overflow-hidden rounded-none border-0 bg-background p-0 shadow-none"
      >
        <header className="shrink-0 border-b border-border px-3 pb-3 pt-[max(0.75rem,env(safe-area-inset-top))] md:px-6 md:pt-4">
          <div className="flex items-center gap-3">
            <Button
              type="button"
              onClick={handleBack}
              variant="outline"
              size="icon"
              className="h-11 w-11 shrink-0 rounded-full"
              aria-label={isModeSelect ? "Close" : "Go back"}
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <DialogHeader className="min-w-0 flex-1 space-y-1 text-left">
              <DialogTitle className="text-left text-lg md:text-xl">
                {titleForMode(mode)}
              </DialogTitle>
              <DialogDescription asChild>
                <p className="text-left text-sm text-muted-foreground">
                  {isModeSelect
                    ? "Pick how you would like to book."
                    : STEP_DESCRIPTIONS[stepId]}
                </p>
              </DialogDescription>
            </DialogHeader>
            <DialogClose asChild>
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="h-11 w-11 shrink-0 rounded-full"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </Button>
            </DialogClose>
          </div>
          {!isModeSelect ? (
            <>
              <div className="mt-3">
                <AppointmentCrumbs />
              </div>
              {stepId === "time" ? (
                <label className="mt-3 flex min-h-11 items-start gap-3 rounded-lg border bg-muted/40 px-3 py-3 text-sm">
                  <input
                    type="checkbox"
                    checked={appointmentStore.activeIsCustomBooking}
                    onChange={(event) =>
                      appointmentStore.setActiveIsCustomBooking(event.target.checked)
                    }
                    className="mt-1 h-4 w-4 shrink-0"
                  />
                  <span>
                    <span className="font-medium text-foreground">Custom booking</span>
                    <span className="mt-0.5 block text-xs text-muted-foreground">
                      {isGroup
                        ? "Submit without fixed times. We will schedule your whole party and confirm."
                        : "Submit without a fixed time. We will review and confirm your schedule."}
                    </span>
                  </span>
                </label>
              ) : null}
            </>
          ) : null}
        </header>

        {isModeSelect ? (
          <div className="min-h-0 flex-1 overflow-y-auto px-3 py-4 md:px-6">
            <BookingModeSelect
              onSelect={(nextMode) => appointmentStore.setBookingMode(nextMode)}
            />
          </div>
        ) : (
          <div className="flex min-h-0 flex-1 flex-col lg:flex-row">
            <div
              className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-3 py-4 md:px-6 lg:w-8/12"
              data-booking-step-scroll
            >
              {stepId === "guests" && <StepGuests />}
              {stepId === "services" && <Step1 />}
              {stepId === "time" && <Step2 />}
              {stepId === "confirm" && <Step3 />}
            </div>
            <div className="shrink-0 border-t border-border bg-background pb-[env(safe-area-inset-bottom)] lg:w-4/12 lg:shrink-0 lg:overflow-hidden lg:border-l lg:border-t-0 lg:pb-0">
              <div className="lg:h-full lg:max-h-none">
                {isGroup ? (
                  <GroupAppointmentCart onCreated={handleCreated} />
                ) : (
                  <AppointmentCart onCreated={handleCreated} />
                )}
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
})
